import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ClientEnv } from 'constants/Chat.constants';

import { projectInit } from 'actions';
import type { TurnMetadata } from 'api/resources/conversation/types';
import type { ChatMessage } from 'api/types';

export type AgentResponseV2 = {
  response: string;
  userInput: string;
  metadata?: TurnMetadata;
};

export type ChatStatus =
  | 'idle' // no conversation, ready to start
  | 'preparing' // preparing draft chat
  | 'starting' // startChatAsync in progress
  | 'ready' // conversation active, accepting messages
  | 'sending' // user message in flight
  | 'waitingSlow' // sending, response taking >1.5s
  | 'ended' // conversation ended
  | 'error'; // error occurred

export interface EnvChatState {
  // Conversation state
  conversationId: string | null;
  messages: ChatMessage[];
  agentResponses: AgentResponseV2[];
  status: ChatStatus;
  requestId: number;
  messageDraft: string;
  endTurn: ChatMessage | null;
  // Per-env config
  variantName: string;
  channel: string;
  languageCode: string | null;
  asrLanguageCode: string | null;
  ttsLanguageCode: string | null;
}

export interface ChatV2State {
  draft: EnvChatState;
  sandbox: EnvChatState;
  'pre-release': EnvChatState;
  live: EnvChatState;
  draftArtifactVersions: {
    artifact?: string;
    lambdaDeployment?: string;
  };
}

export const NO_VARIANT_NAME_V2 = 'No variant';
const DEFAULT_CHANNEL = 'chat.polyai';

export const initialEnvChatState: EnvChatState = {
  conversationId: null,
  messages: [],
  agentResponses: [],
  status: 'idle',
  requestId: 0,
  messageDraft: '',
  endTurn: null,
  variantName: NO_VARIANT_NAME_V2,
  channel: DEFAULT_CHANNEL,
  languageCode: null,
  asrLanguageCode: null,
  ttsLanguageCode: null,
};

export const initialChatV2State: ChatV2State = {
  draft: { ...initialEnvChatState },
  sandbox: { ...initialEnvChatState },
  'pre-release': { ...initialEnvChatState },
  live: { ...initialEnvChatState },
  draftArtifactVersions: {},
};

/** Reset conversation fields only, preserving per-env config */
function resetConversation(envState: EnvChatState): EnvChatState {
  return {
    ...envState,
    conversationId: null,
    messages: [],
    agentResponses: [],
    status: 'idle',
    requestId: envState.requestId + 1,
    messageDraft: '',
    endTurn: null,
    asrLanguageCode: null,
    ttsLanguageCode: null,
  };
}

type EnvPayload = { clientEnv: ClientEnv };
type RequestIdPayload = { requestId: number } & EnvPayload;

const chatV2Slice = createSlice({
  name: 'chatV2',
  initialState: initialChatV2State,
  reducers: {
    beginPreparing: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'idle') return;
      state[clientEnv].status = 'preparing';
      state[clientEnv].requestId += 1;
    },

    beginStarting: (
      state,
      { payload: { clientEnv } }: PayloadAction<EnvPayload>,
    ) => {
      const env = state[clientEnv];
      if (env.status !== 'idle' && env.status !== 'preparing') return;
      if (env.status === 'idle') {
        env.requestId += 1;
      }
      env.status = 'starting';
    },

    chatStarted: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          agentResponse,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          agentResponse: AgentResponseV2;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'starting') return;
      const env = state[clientEnv];
      env.status = 'ready';
      env.conversationId = conversationId;
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
    },

    beginSending: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: ChatMessage } & EnvPayload>,
    ) => {
      if (state[clientEnv].status !== 'ready') return;
      state[clientEnv].messages.push(message);
      state[clientEnv].messageDraft = '';
      state[clientEnv].status = 'sending';
      state[clientEnv].requestId += 1;
    },

    setWaitingSlow: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].status !== 'sending') return;
      state[clientEnv].status = 'waitingSlow';
    },

    messageSent: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      state[clientEnv].status = 'ready';
    },

    chatEnded: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      state[clientEnv].status = 'ended';
    },

    chatErrored: (
      state,
      { payload: { clientEnv, requestId } }: PayloadAction<RequestIdPayload>,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      const { status } = state[clientEnv];
      if (
        status !== 'preparing' &&
        status !== 'starting' &&
        status !== 'sending' &&
        status !== 'waitingSlow'
      ) {
        return;
      }
      state[clientEnv].status = 'error';
    },

    responseReceived: (
      state,
      {
        payload: {
          clientEnv,
          requestId,
          conversationId,
          agentMessage,
          previousMessageMetadata,
          agentResponse,
          conversationEnded,
          asrLanguageCode,
          ttsLanguageCode,
        },
      }: PayloadAction<
        RequestIdPayload & {
          conversationId: string;
          agentMessage: ChatMessage;
          previousMessageMetadata: TurnMetadata;
          agentResponse: AgentResponseV2;
          conversationEnded?: boolean;
          asrLanguageCode?: string;
          ttsLanguageCode?: string;
        }
      >,
    ) => {
      if (state[clientEnv].requestId !== requestId) return;
      if (state[clientEnv].conversationId !== conversationId) return;
      const { status } = state[clientEnv];
      if (status !== 'sending' && status !== 'waitingSlow') return;
      const env = state[clientEnv];
      if (asrLanguageCode) env.asrLanguageCode = asrLanguageCode;
      if (ttsLanguageCode) env.ttsLanguageCode = ttsLanguageCode;
      const previousMessage = env.messages.at(-1);
      if (previousMessage) {
        previousMessage.metadata = previousMessageMetadata;
      }
      env.messages.push(agentMessage);
      env.agentResponses.push(agentResponse);
      if (conversationEnded) env.status = 'ended';
    },

    setChatMessageDraftV2: (
      state,
      {
        payload: { clientEnv, message },
      }: PayloadAction<{ message: string } & EnvPayload>,
    ) => {
      state[clientEnv].messageDraft = message;
    },

    setEndTurnsV2: (
      state,
      {
        payload: { clientEnv, message, metadata },
      }: PayloadAction<
        { message: string; metadata: TurnMetadata } & EnvPayload
      >,
    ) => {
      state[clientEnv].endTurn = {
        text: message,
        from: 'agent',
        metadata,
      };
    },

    /** Updates variant for a specific env and resets its conversation */
    setChatVariantNameV2: (
      state,
      {
        payload: { clientEnv, variantName },
      }: PayloadAction<{ variantName: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].variantName === variantName) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        variantName,
      };
    },

    /** Updates language code for a specific env and resets its conversation */
    setChatLanguageCodeV2: (
      state,
      {
        payload: { clientEnv, languageCode },
      }: PayloadAction<{ languageCode: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].languageCode === languageCode) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        languageCode,
      };
    },

    /** Updates channel for a specific env and resets its conversation */
    setChatChannelV2: (
      state,
      {
        payload: { clientEnv, channel },
      }: PayloadAction<{ channel: string } & EnvPayload>,
    ) => {
      if (state[clientEnv].channel === channel) return;
      state[clientEnv] = {
        ...resetConversation(state[clientEnv]),
        channel,
      };
    },

    /** Resets conversation for a specific env while preserving config */
    resetChatV2: (
      state,
      { payload }: PayloadAction<{ clientEnv?: ClientEnv } | undefined>,
    ) => {
      if (!payload?.clientEnv) {
        // Reset all envs
        return { ...initialChatV2State };
      }
      state[payload.clientEnv] = resetConversation(state[payload.clientEnv]);
    },

    setDraftArtifactVersionsV2: (
      state,
      {
        payload: { artifactVersion, lambdaDeployment },
      }: PayloadAction<{
        artifactVersion: string;
        lambdaDeployment?: string;
      }>,
    ) => {
      state.draftArtifactVersions = {
        artifact: artifactVersion,
        lambdaDeployment,
      };
    },

    resetDraftArtifactVersionsV2: state => {
      state.draftArtifactVersions = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(projectInit, () => ({ ...initialChatV2State }));
  },
});

export const {
  beginPreparing,
  beginStarting,
  chatStarted,
  beginSending,
  setWaitingSlow,
  messageSent,
  chatEnded,
  chatErrored,
  responseReceived,
  setChatMessageDraftV2,
  setEndTurnsV2,
  setChatVariantNameV2,
  setChatLanguageCodeV2,
  setChatChannelV2,
  resetChatV2,
  setDraftArtifactVersionsV2,
  resetDraftArtifactVersionsV2,
} = chatV2Slice.actions;

export default chatV2Slice.reducer;
